import axios from 'axios'
import { parse } from 'date-fns'
import { NextSeo } from 'next-seo'
import { GetStaticPaths, GetStaticProps } from 'next'

import { PostData } from '@models/blog'
import { Footer } from '@components/Footer'
import { Header } from '@components/Header'
import Nav from '@components/BlogContainer/Nav'
import BlogPost from '@components/BlogContainer/BlogPost'
import SideBar from '@components/BlogContainer/SideBar/index'

interface Props {
  post: PostData
}

export default function Post({ post }: Props) {
  let width = 771
  let height = 690

  // Extracts dimensions from WP asset URL
  const possibleDimensions = post.featuredImg.large.match(/(\d+)x(\d+)/)

  if (possibleDimensions?.length) {
    const [, widthString, heightString] = possibleDimensions

    width = parseInt(widthString)
    height = parseInt(heightString)
  }

  return (
    <>
      <NextSeo
        title={post.title.rendered}
        description={post?.excerpt}
        canonical={`https://majordomo.com/blog/${post.slug}`}
        openGraph={{
          url: `https://majordomo.com/blog/${post.slug}`,
          title: `${post.title.rendered} | Majordomo`,
          description: `${post.excerpt}`,
          images: [
            {
              url: `${post.featuredImg.large}`,
              width,
              height,
              alt: `Featured image for ${post.title.rendered}`
            }
          ],
          type: 'article',
          article: {
            publishedTime: parse(
              post.publishDate,
              'y-MM-dd HH:mm:ss',
              new Date()
            ).toISOString(),
            section: post.category.length ? post.category[0].name : undefined,
            tags: post.tags ? post.tags.map(tag => tag.name) : undefined,
            authors: [
              `https://majordomo.com/blog/author/${post.author.name
                .toLowerCase()
                .replace(/ /, '-')}-${post.author.id}`
            ]
          }
        }}
      />

      <Header />
      <Nav categorySlug="/" />
      <div className="container max-w-6xl px-0 mx-auto mt-8 mb-24">
        <div className="flex flex-col px-8 lg:flex-row blog-container">
          <div className="blog-section">
            <BlogPost {...post} />
          </div>
          <div className="flex-1 lg:pl-12">
            <SideBar {...post} />
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await axios.get(
    `${process.env.NEXT_PUBLIC_BLOG_URL}/wp-json/wpr/v1/posts`
  )
  const paths = data.map((post: PostData) => ({
    params: { slug: post.slug.toString() }
  }))

  return {
    paths,
    fallback: false
  }
}

export const getStaticProps: GetStaticProps = async ctx => {
  const slug = ctx.params !== undefined ? ctx.params.slug : ''
  const { data: post } = await axios.get(
    `${process.env.NEXT_PUBLIC_BLOG_URL}/wp-json/wpr/v1/posts/${slug}`
  )

  return {
    props: {
      post
    }
  }
}
